import { useState } from 'react'
import { isApiError } from '@/api'
import { Field, Input } from '@/ui'
import { errorMessage } from '../../api/core'
import { reauthenticate } from '../../api/reauth'
import { useRecall } from '../../api/recall'
import type { RecallExport } from '../../model/recall/mappers'
import { recallReach } from '../../model/recall/mappers'
import { recallScopeSentence, validateRecallForm } from '../../model/recall/recallForm'
import { unitTypeLabel } from '../../model/recall/words'
import { ConfirmDialog } from '../ConfirmDialog'
import { Code, Status } from '../bits'

/**
 * Recall (F7.9): every pack lot found from this code goes on recall hold. Signed with the person's
 * own password (re-authentication) and a reason.
 */
export function RecallDialog({ exp, open, onClose }: { exp: RecallExport; open: boolean; onClose: () => void }) {
  const [password, setPassword] = useState('')
  const [pwError, setPwError] = useState<string | null>(null)
  const [signing, setSigning] = useState(false)
  const [reasonError, setReasonError] = useState<string | null>(null)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setPassword('')
      setPwError(null)
      setReasonError(null)
    }
  }
  const recall = useRecall()
  const reach = recallReach(exp)
  const exact = exp.start.unitType === 'PACK' || exp.start.unitType === 'LOT'
  const affected = exp.start.unitType === 'PACK' ? exp.packLots.filter((p) => p.id === exp.start.packLotId) : exp.packLots

  const confirm = async (reason: string) => {
    const errs = validateRecallForm({ reason, password })
    if (errs.password) {
      setPwError(errs.password)
      return
    }
    if (errs.reason) {
      setReasonError(errs.reason)
      return
    }
    setReasonError(null)
    setSigning(true)
    let token: string
    try {
      token = await reauthenticate(password)
    } catch (e) {
      setPwError(isApiError(e) && (e.status === 401 || e.status === 400) ? 'That password is not right' : errorMessage(e))
      setSigning(false)
      return
    }
    try {
      await recall.mutateAsync({ code: exp.start.code, reason, reauthToken: token })
      onClose()
    } finally {
      setSigning(false)
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={`Recall ${exp.start.code}`}
      description={`${unitTypeLabel(exp.start.unitType)} · ${reach.customers} ${reach.customers === 1 ? 'customer' : 'customers'} reached`}
      confirmLabel="Recall"
      tone="danger"
      loading={signing || recall.isPending}
      reason={{ label: 'Reason', minLength: 10, placeholder: 'Why this is being recalled', hint: 'At least 10 characters; it goes on every pack lot’s record.' }}
      onConfirm={confirm}
    >
      <p className="text-[14.5px] text-ink">
        {recallScopeSentence(exp.start.unitType, affected.length, exact ? affected.reduce((s, p) => s + p.pouchesDispatched, 0) : 0)}
      </p>
      {!exact && affected.length > 0 && <p className="text-[13px] text-muted">Pack lots in this trace (only those made from this code are held):</p>}
      {affected.length > 0 ? (
        <ul className="flex max-h-48 flex-col gap-1 overflow-auto rounded-card border border-rule px-3 py-2">
          {affected.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 text-[13.5px]">
              <Code>{p.code}</Code>
              <span className="text-muted">
                {p.pouches ?? 0} pouches · {p.pouchesDispatched} with customers
              </span>
              <span className="ml-auto">
                <Status status={p.hold === 'NONE' ? p.status : p.hold} />
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-muted">No pack lots have been made from this code yet; the batches and lots are marked recalled.</p>
      )}
      {reasonError && <p className="text-[13px] font-medium text-danger">Reason: {reasonError}</p>}
      <Field label="Your password" required error={pwError} hint="You are signing this recall.">
        <Input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value)
            setPwError(null)
          }}
        />
      </Field>
    </ConfirmDialog>
  )
}
